import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from "@angular/core";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { SortDirection } from "@app/enum/SortDirection";
import { VTableSelectionType } from "@app/enum/VTableSelectionType";
import { ResponsiveService } from "@app/services/responsive.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, Subscription } from "rxjs";
import { VTableHeaderType } from "../_enums/VTableHeaderType.enum";
import { IVTable } from "../_models/IVTable";
import { IVTableCell } from "../_models/IVTableCell";
import { IVTableColumn } from "../_models/IVTableColumn";
import { IVTableHeader } from "../_models/IVTableHeader";
import { IVTableRow } from "../_models/IVTableRow";
import { VTableService } from "../_services/v-table.service";
import { VTableHeader } from "./cells/header/v-table-header.component";
import './v-table.component.scss';

@UntilDestroy()
@Component({
    selector: 'v-table',
    template: require('./v-table.component.html')
})
export class VTableComponent {
    @Input() tableRows: IVTableRow[];
    @Input() tableColumns: IVTableColumn<any>[];
    @Input() loading: boolean;
    @Input() trueSize: boolean; // When set, this will set the tables height to the exact required to ensure no scrolling
    @Input('observable') rowDataObservable: Observable<any>;

    @Input() selectionType: VTableSelectionType = VTableSelectionType.None;
    @HostBinding('class.clickable') @Input() clickable: boolean;
    @Output() rowClick = new EventEmitter<IVTableRow>();

    @Output() tableRowsChange = new EventEmitter<IVTableRow[]>();
    @Output() tableDisplayRowsChange = new EventEmitter<IVTableRow[]>();
    @Output() selectedRowsChange = new EventEmitter<IVTableRow[]>();

    @ViewChild("viewport") viewport: CdkVirtualScrollViewport;
    @ViewChildren(VTableHeader) tableHeaders: QueryList<VTableHeader>;

    SortDirection = SortDirection;

    table: IVTable;
    selectedRows: IVTableRow[];

    vTableSelectionType = VTableSelectionType;

    subscriptions = new Subscription();

    allRowsSelected: boolean;
    rowHeight: number = 38;

    showCheckboxColumn: boolean;

    currentWidth: number;
    canEditColumns: boolean = false;
    showColumnEditor: boolean = false;

    private visibilityObserver: IntersectionObserver;
    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly el: ElementRef,
        private readonly filterBarService: FilterBarService,
        private readonly responsiveService: ResponsiveService,
        private readonly vTableService: VTableService) {
    };

    ngOnInit(): void {
        this.setupSubscriptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tableRows'] && this.tableRows) {
            this.handleNewTableRows();
        }

        if (changes['selectionType']) {
            this.showCheckboxColumn = this.selectionType === VTableSelectionType.Multi;
        }

        if (changes['rowDataObservable']) {
            this.subscribeToRowDataChanges();
        }

        if ((this.rowClick.observers.length != 0  || this.selectedRowsChange.observers.length != 0) && this.clickable === undefined) {
            this.clickable = true;
        }
    }

    ngAfterViewInit(): void {
        this.subscriptions.add(
            this.tableHeaders.changes.subscribe(changes => {
                this.render();
            })
        )
    }

    ngOnDestroy(): void {
        this.subscriptions?.unsubscribe();
        this.visibilityObserver.unobserve(this.el.nativeElement);
    }

    toggleSortOrder(header: IVTableHeader): void {
        if (!header.sortable) {
            return;
        }

        this.table.headers.forEach((tableHeader: IVTableHeader) => {
            if (tableHeader.id === header.id) {
                return;
            }
            tableHeader.sorted = false;
            tableHeader.sortDirection = SortDirection.Ascending;
        });

        header.sorted = true;

        if (header.sortDirection === SortDirection.Ascending) {
            header.sortDirection = SortDirection.Descending;
        }
        else {
            header.sortDirection = SortDirection.Ascending;
        }

        this.applyColumnSorting();
    }

    closeColumnEditor(): void {
        this.filterBarService.toggleTableColumnEditor(false, this.tableColumns);
    }

    applyColumnSorting(): void {
        if (!this.table?.headers || !this.table?.rows) {
            return;
        }
        let sortedHeader = this.getSortedHeader();

        if (!sortedHeader) {
            return;
        }

        this.table.rows = [...this.vTableService.sortColumn(this.table, sortedHeader).rows];
        this.bringSelectedRowsToTop();
        this.setDisplayRows();
    }

    onChildrenRendered(): void {
        this.viewport?.checkViewportSize();
    }

    trackByRow(index: number, row: IVTableRow): number {
        return row.id;
    }

    trackByCell(index: number, cell: IVTableCell): number {
        return cell.id;
    }

    onRowClicked(tableRow: IVTableRow): void {
        switch (this.selectionType) {
            case VTableSelectionType.Single:
                this.table?.rows?.forEach(row => row.selected = false);
                tableRow.selected = true;
                this.rowClick.emit(tableRow);
                break;
            case VTableSelectionType.Multi:
                if (!tableRow.disableSelect) {
                    this.toggleRow(tableRow);
                }
                break;
            default:
                this.rowClick.emit(tableRow);
                break;
        }
    }

    onCellClicked(cell: IVTableCell, row: IVTableRow): void {
        if(!cell?.disableCell) {
            this.onRowClicked(row);
        }        
    }

    selectAllRows(): void {
        if (!(this.selectionType == VTableSelectionType.Multi)) {
            return;
        }

        let selectAll = false;
        const selectableRows = this.table.displayRows.filter(row => !row.disableSelect);

        if (this.selectedRows.length < selectableRows.length) {
            selectAll = true;
        }

        this.toggleAllRows(selectAll);
    }

    render(): void {
        this.viewport?.checkViewportSize();
    }

    addNewRow(data): void {
        const newRow = this.vTableService.createTableRow(data, this.tableColumns);
        newRow.class = "new";

        this.table.rows = [...this.tableRows, newRow];
        this.tableRowsChange.emit(this.table.rows);
        this.cd.detectChanges();
    }

    getWidth(): number {
        return this.currentWidth;
    }

    //--

    private setupTable(): void {
        if (!this.tableColumns) {
            return;
        }

        this.selectedRows = [];
        this.checkCanEdit();

        this.table = this.createTable();
        this.setTableHeaderIds();
        this.setTableRowIds();
        this.setDisplayRows();
        this.linkHeadersToCells();

        if (this.trueSize) {
            this.setTrueSize();
        }

        this.render();
    }

    private checkCanEdit(): void {
        this.canEditColumns = this.tableColumns.some(col => col.header.canEdit);

        if (!this.canEditColumns) {
            return;
        }

        this.tableColumns = this.vTableService.getTableColumnPreferences(this.tableColumns);
    }

    private setTrueSize(): void {
        const element = this.el.nativeElement as HTMLElement;
        const numRows = this.table?.rows?.length || 5; // Assume height of 5 rows to allow for empty state component
        let height = this.rowHeight * (numRows + 1); // plus one for header
        height++; // Plus one to account for one final border

        element.style.minHeight = `${height}px`;
        element.style.maxHeight = `${height}px`;
        element.classList.add("noOverflow");
    }

    private setDisplayRows(): void {
        if (!this.table) {
            return;
        }
        
        this.table.displayRows = this.table.rows?.filter(row => !row.hide) || [];
        this.tableDisplayRowsChange.emit(this.table.displayRows);
        this.updateSelectedRows();
    }

    private createTable(): IVTable {
        this.setupMultiSelect();
        return {
            id: this.vTableService.getTableId(this.tableColumns),
            headers: this.tableColumns.map(column => column.header),
            rows: this.tableRows
        }
    }

    private setupMultiSelect(): void {
        if (!(this.selectionType == VTableSelectionType.Multi) || this.selectedRows?.length) {
            return;
        }

        this.selectedRows = [];

        this.tableRows?.forEach(row => {
            if (row.selected) {
                this.selectedRows.push(row);
            }
        })
    }

    private clearAllSelectedRows(): void {
        this.table?.rows?.forEach(row => row.selected = false);
    }

    private getSortedHeader(): IVTableHeader {
        let sortedHeader = this.table.headers.find((header: IVTableHeader) => header.sorted);
        if (!sortedHeader) {
            // If no sorted column specified use the first available sortable column
            sortedHeader = this.table.headers.find((header: IVTableHeader) => header.sortable);
        }
        return sortedHeader;
    }

    private updateSelectedRows(): void {
        if (!(this.selectionType == VTableSelectionType.Multi) || !this.table?.displayRows || !this.selectedRows?.length) {
            return;
        }
        this.clearAllSelectedRows();

        let markedForRemoval: number[] = [];

        this.selectedRows.forEach((row, index) => {
            const rowInTable = this.table.rows.find(r => r.id === row.id || r.data.Id === row.data.Id);
            if (rowInTable) {
                rowInTable.selected = true;
            }
            else {
                markedForRemoval.push(index);
            }
        });

        markedForRemoval.forEach(index => {
            this.selectedRows.splice(index, 1);
        })

        this.setAllRowsSelected();
        this.selectedRowsChange.emit(this.selectedRows);

        return;
    }

    private setTableHeaderIds(): void {
        if (!this.table?.headers) {
            return;
        }

        let index = 0;
        this.table.headers.forEach((header: IVTableHeader) => {
            if (header.id || header.type === VTableHeaderType.Checkbox) {
                return;
            }
            header.id = index;
            index++;
        });
    }

    private setTableRowIds(): void {
        if (!this.table?.rows) {
            return;
        }

        this.table.rows.forEach((row, index) => {
            if (row.id) {
                return;
            }
            row.id = index;
        });
    }


    private linkHeadersToCells(): void {
        if (!this.table?.rows) {
            return;
        }
        let accumulator = 0;

        this.table.rows.forEach((row, rowIndex) => {
            row.cells.forEach((cell, cellIndex) => {
                cell.id = accumulator;
                accumulator++;
                cell.header = this.tableColumns[cellIndex].header
            });
        })
    }

    private toggleRow(row: IVTableRow): void {
        row.selected = !row.selected;

        const selectedRowIndex = this.selectedRows.indexOf(row);

        if (selectedRowIndex >= 0) {
            this.selectedRows.splice(selectedRowIndex, 1);
        }
        else {
            this.selectedRows.push(row);
        }

        this.bringSelectedRowsToTop();

        this.setDisplayRows();
        this.setAllRowsSelected();
        this.selectedRowsChange.emit(this.selectedRows);
    }

    private toggleAllRows(selectAll: boolean): void {
        if (this.selectionType !== VTableSelectionType.Multi || !this.table) {
            return;
        }

        this.selectedRows = [];

        this.table.displayRows.forEach(row => {
            if (row.disableSelect) {
                return;
            }

            row.selected = selectAll;
            if (selectAll) {
                this.selectedRows.push(row);
            }
        });

        this.setDisplayRows();
        this.setAllRowsSelected();
        this.selectedRowsChange.emit(this.selectedRows);
    }

    private setAllRowsSelected(): void {
        const selectableRows = this.table.displayRows.filter(row => !row.disableSelect);

        this.allRowsSelected = this.selectedRows.length && this.selectedRows.length === selectableRows.length;
    }

    private setupSubscriptions(): void {
        this.subscriptions.add(this.filterBarService.dataChanged.subscribe(() => {
            this.setDisplayRows();
        }));

        this.subscriptions.add(this.filterBarService.tableColumnEditorOpen.subscribe({
            next: ({ open, tableColumns }) => {
                if (!this.table) {
                    return;
                }

                if (this.vTableService.getTableId(tableColumns) == this.table.id) {
                    this.showColumnEditor = open;
                }
            }
        }))

        this.setupVisibilityObserver();
    }

    private setupVisibilityObserver(): void {
        // Ensure cdk-scroller sets the correct viewport height when table is brought
        // into view after loading, i.e.on tab change
        this.visibilityObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.render();
                }
            });
        });

        this.subscriptions.add(this.responsiveService.currentWindowWidth.subscribe(() => {
            this.currentWidth = (this.el.nativeElement as HTMLElement)?.clientWidth;
            this.cd.markForCheck();
        }));

        this.visibilityObserver.observe(this.el.nativeElement);
    }

    private handleNewTableRows(): void {
        if (!this.tableRows) {
            return;
        }

        this.setupTable();
        this.applyColumnSorting();

        this.viewport?.checkViewportSize();

        this.tableRowsChange.emit(this.table.rows);
    }

    private bringSelectedRowsToTop(): void {
        if (this.selectionType === VTableSelectionType.None) {
            return;
        }

        this.table.rows = this.table.rows.filter(row => !row.selected);

        this.table.rows.unshift(...this.selectedRows);
    }

    private subscribeToRowDataChanges(): void {
        if (!this.rowDataObservable) {
            return;
        }

        this.loading = true;

        this.rowDataObservable.pipe(untilDestroyed(this)).subscribe(data => {
            this.tableRows = this.vTableService.createTableRows(data, this.tableColumns);
            this.handleNewTableRows();
            this.loading = false;
        })
    }
}
